import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core';
import {ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR,} from '@angular/forms';

export interface Option {
  label: string;
  value: any;
}

export interface OptionGroup {
  label: string;
  value: any;
  items: Option[];
}

@Component({
  selector: 'app-core-dropdown',
  templateUrl: './core-dropdown.component.html',
  styleUrls: ['./core-dropdown.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CoreDropdownComponent),
      multi: true,
    },
  ],
})
export class CoreDropdownComponent
  implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
  @Input() options: Option[] | OptionGroup[] | string[] | any[] = [];
  @Input() type: 'group' | 'single' | 'string' = 'single';
  @Input() response: 'both' | 'value' = 'value';
  @Input() version: 'form' | 'ngModel' = 'ngModel';
  @Input() placeholder: string = '';
  @Input() disabled: boolean = false;
  @Input() formGroupParent: FormGroup;
  @Input() formControlName: string;
  @Input() value: any;
  @Input() filter: boolean = true;
  @Input() fetchGroupInfo:boolean=false; // returns value with group info

  searchText: string = '';

  @Output() valueChange = new EventEmitter<any>();
  @Output() changeOn = new EventEmitter<any>();

  isOpen = false;
  filteredOptions: Option[] | OptionGroup[] | string[] | any[] = [];
  selectedLabel: string | null = null;
  selectedValue: string | null = null;
  private propagateChange = (_: any) => {
  };
  private propagateTouched = () => {
  };

  writeValue(value: any): void {
    this.value = value?.value ?? value;
    this.setSelectedLabel();
    this.propagateChange(value);
  }

  private clickListener: (() => void) | null = null;

  constructor(private readonly eRef: ElementRef, private readonly renderer: Renderer2) {
  }

  ngOnInit() {
    this.filteredOptions = this.options;
    if (this.version === 'form') {
      if (this.formGroupParent && this.formControlName) {
        this.formGroupParent.get(
          this.formControlName
        ) as FormControl;
      }
    } else {
      this.setSelectedLabel();
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options'] && !changes['options'].firstChange) {
      this.filteredOptions = this.options;
      this.setSelectedLabel();
    }

    if (
      this.version === 'ngModel' &&
      changes['value'] &&
      !changes['value']?.firstChange
    ) {
      this.setSelectedLabel();
    }
  }

  /* commont functions */
  toggleDropdown() {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.addClickListener();
    } else {
      this.removeClickListener();
    }
  }


  private addClickListener() {
    this.clickListener = this.renderer.listen('document', 'click', (event: Event) => {
      if (!this.eRef.nativeElement.contains(event.target)) {
        this.isOpen = false;
        this.removeClickListener();
      }
    });
  }

  private removeClickListener() {
    if (this.clickListener) {
      this.clickListener = null;
    }
  }

  ngOnDestroy() {
    this.removeClickListener();
  }

  private setSelectedLabel(): void {
    if (this.value !== undefined && this.value !== null) {
      const foundOption = this.findOptionByValue(this.value);
      if (foundOption) {
        this.selectedLabel = foundOption.label;
        this.selectedValue = foundOption.value;
      } else {
        this.selectedLabel = null; this.selectedValue = null;
      }
    } else {
      this.selectedLabel = null; this.selectedValue = null;
    }
  }

  private findOptionByValue(value: any): Option | undefined {
    const val = value?.value ?? value;
    if (this.type === 'group') {
      for (const group of this.options as OptionGroup[]) {
        const found = group.items.find((item) => item.value == val);
        if (found) {
          return found;
        }
      }
    } else if (this.type === 'single') {
      const found = (this.options as Option[]).find(
        (option) => option.value == val
      );
      if (found) {
        return found;
      }
    } else if (this.type === 'string') {
      const found = (this.options as string[]).find((option) => option === val);
      if (found) {
        return {label: found, value: found};
      }
    }
    return undefined;
  }

  /* common functions */

  /* Group dropdown functions */
  selectGroupOption(
    option: Option | OptionGroup | any,
    group?: OptionGroup
  ): void {
    this.selectedLabel = option.label;
    this.selectedValue = option.value;
    let response = null;
    if (this.response === 'both') {
      if (this.fetchGroupInfo) {
        response = {
          group: {label: group?.label, value: group?.value},
          label: this.selectedLabel,
          value: this.selectedValue,
        };
      } else {
        response = {
          label: this.selectedLabel,
          value: this.selectedValue
        }
      }
    } else {
      if (this.fetchGroupInfo) {
        response = {
          group: group?.value,
          value: this.selectedValue,
        };
      } else {
        response = this.selectedValue;
      }
      ;
    }
    if (this.version === 'form') {
      this.formGroupParent.patchValue({[this.formControlName]: response});
    } else {
      this.valueChange.emit(response);
    }
    this.changeOn.emit(response);
    this.isOpen = false;
  }

  filterGroupOptions(): void {
    const filterValue = this.searchText.toLowerCase();
    this.filteredOptions = this.options
      .map((group) => ({
        ...group,
        items: group.items.filter((item: Option) =>
          item.label.toLowerCase().includes(filterValue)
        ),
      }))
      .filter((group) => group.items.length > 0);
  }

  /* Group dropdown functions */

  /* dropdown functions */
  selectOption(option: Option | OptionGroup | any): void {
    this.selectedLabel = option.label;
    this.selectedValue = option.value;
    let response = null;
    if (this.response === 'both') {
      this.value = {label: this.selectedLabel, value: this.selectedValue};
      response = {
        label: this.selectedLabel,
        value: this.selectedValue,
      };
    } else {
      this.value = this.selectedValue;
      response = this.selectedValue;
    }

    if (this.version === 'form') {
      this.formGroupParent.patchValue({[this.formControlName]: response});
    } else {
      this.valueChange.emit(response);
    }
    this.changeOn.emit(response);
    this.isOpen = false;
  }

  filterOptions(): void {
    const filterValue = this.searchText.toLowerCase();
    if (this.type === 'single') {
      this.filteredOptions = this.options.filter((option) =>
        option.label.toLowerCase().includes(filterValue)
      );
    } else if (this.type === 'string') {
      this.filteredOptions = this.options.filter((option) =>
        option.toLowerCase().includes(filterValue)
      );
    }
  }

  /* dropdown functions */

  /* string dropdown functions functions */
  selectStringOption(option: string): void {
    this.selectedLabel = option;
    this.selectedValue = option;
    this.value = this.selectedValue;
    if (this.version === 'form') {
      this.formGroupParent.patchValue({
        [this.formControlName]: this.selectedValue,
      });
    } else {
      this.valueChange.emit(this.selectedValue);
    }
    this.changeOn.emit(this.selectedValue);
    this.isOpen = false;
  }

  /* string dropdown functions functions */

  reset() {
    this.selectedLabel = null;
    this.selectedValue = null;
    this.value = null;
    this.isOpen = false;
    if (this.version === 'form') {
      this.formGroupParent.patchValue({[this.formControlName]: null});
    } else {
      this.valueChange.emit(this.selectedValue);
    }
    this.changeOn.emit(this.selectedValue);
  }
}
